
import { Component, Input, OnInit, Output } from '@angular/core';

import { MessageService } from 'primeng/api';
import { debounceTime, map, of } from 'rxjs';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';

import { JournodexSubjectsService } from '@Common/Journodex/services/journodex-subjects.service';
import { TargetingPreferencesService } from '@Common/TargetingShared/services/targeting-preferences.service';
import { TargetingSearchFormService } from '@Common/TargetingShared/services/targeting-search-form.service';
import { TargetingGeographyService } from '@Common/TargetingShared/services/targeting-geography-service';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { AgprComponentStyleEnum } from '@Common/Basic/enums/social-analysis-types.enum';
import { IncludeExcludeModel } from '@Common/Misc/models/include-exclude-model';

import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { TargetingSearchesService } from '@Discover/Targeting/services/targeting-searches.service';
import { TargetingSearchComponent } from '@Discover/Targeting/components/TargetingSearch/targeting-search.component';
import { DiscoverRoutingService } from '@Discover/DiscoverRouter/services/discover-routing.service';
import { TargetingSearchFullModel } from '@Discover/Targeting/models/targeting-search-full.model';
import { DistributionPickerService } from '@Common/RhpContexts/services/distribution-picker.service';


@Component({
    selector: 'agpr-quick-edit-refinements-search',
    templateUrl: './quick-edit-refinements-search.component.html',
    styleUrls: ['./quick-edit-refinements-search.component.scss'],
    standalone: false
})
export class QuickEditRefinementsSearchComponent extends TargetingSearchComponent implements OnInit {
    readonly messageSeverities = messageSeverityEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() set searchParamsVisible (searchParamsVisible: any) {
        this._searchParamsVisible = searchParamsVisible;
        this.checkIfEmpty();

    }
    _searchParamsVisible: any;
    @Input() refinementSearchParams: DropdownOptionModel[] = [];
    @Input() set refinementData(search: TargetingSearchFullModel) {
        this.searchForm.patchValue({
            general: {
                mediaTypes: search.mediaTypes,
                productTypes: search.productTypes,
                marketSectors: search.marketSectors,
                coverageFocuses: search.coverageFocuses,
                productFrequencies: search.productFrequencies,
                targetAudiences: search.targetAudiences
            }
        }, { emitEvent: false, onlySelf: true});
        this.checkIfEmpty();
    }

    @Output() formReady = of(this.searchForm);

    @Output() valueChange = this.searchForm.valueChanges.pipe(
        // @hack - fixes timing issues between valueChanges, patchValue
        debounceTime(300),
        map(
            (formValue): Partial<TargetingSearchFullModel> => {
                return {
                    mediaTypes: formValue.general.mediaTypes,
                    productTypes: formValue.general.productTypes,
                    marketSectors: formValue.general.marketSectors,
                    coverageFocuses: formValue.general.coverageFocuses,
                    productFrequencies: formValue.general.productFrequencies,
                    targetAudiences: formValue.general.targetAudiences,
                };
            }
        ),
    );

    refinementParams: string [] = ['mediaTypes', 'productTypes', 'marketSectors', 'coverageFocuses', 'productFrequencies', 'targetAudiences'];

    isEmpty: boolean = false;

    constructor(
        public journodexSubjectsService: JournodexSubjectsService,
        protected messageService: MessageService,
        public targetingSearchesService: TargetingSearchesService,
        protected discoverRoutingService: DiscoverRoutingService,
        public journodexEnumsService: JournodexEnumsService,
        public targetingGeographyService: TargetingGeographyService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        protected targetingPreferencesService: TargetingPreferencesService,
        protected targetingSearchFormService: TargetingSearchFormService,
        public distributionPickerService: DistributionPickerService,
    ) {
        super(journodexSubjectsService,
            messageService,
            targetingSearchesService,
            discoverRoutingService,
            journodexEnumsService,
            targetingGeographyService,
            localizationService,
            coreDialogService,
            currentUserStateService,
            targetingPreferencesService,
            targetingSearchFormService,
            distributionPickerService
        );
    }

    ngOnInit() {
        // do nothing, override super
    }

    handleRefinementChange(event) {
        this._searchParamsVisible[event.value?.value] = true;
        const index = this.refinementSearchParams.findIndex(param => param.value === event.value?.value);
        if (index > -1) {
            this.refinementSearchParams.splice(index, 1);
        }
        this.checkIfEmpty();
    }

    checkIfEmpty() {
        let isEmpty = true;
        for (const param of this.refinementParams) {
            if (this._searchParamsVisible[param]) {
                isEmpty = false;
                break;
            }
        }
        this.isEmpty = isEmpty;
    }

    removeParam(key: string) {
        const blankModel: IncludeExcludeModel<any> = new IncludeExcludeModel();
        this.searchForm.get('general').get(key).patchValue(blankModel);
        this._searchParamsVisible[key] = false;
        this.checkIfEmpty();
    }
}
